import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
    /**
     * Specify your server-side environment variables schema here. This way you can ensure the app
     * isn't built with invalid env vars.
     */
    server: {
        DATABASE_URL: z.string().url(),
        SLACK_CUSTOMER_NOTIFIER_WEBHOOK_URL: z.string().url(),
        LOCAL_LONGITUDE: z.string(),
        LOCAL_LATITUDE: z.string(),
        NODE_ENV: z.enum(["development", "test", "production"]),
        VERCEL_ENV: z.enum(["development", "preview", "production"]),
        NEXTAUTH_SECRET:
            process.env.NODE_ENV === "production"
                ? z.string().min(1)
                : z.string().min(1).optional(),
        NEXTAUTHJWT_SECRET: z.string().min(1),
        NEXTAUTH_URL:
            process.env.NODE_ENV === "production"
                ? z.string().optional()
                : z.string().url(),
        // Add `.min(1) on ID and SECRET if you want to make sure they're not empty

        GOOGLE_CLIENT_ID: z.string(),
        GOOGLE_CLIENT_SECRET: z.string(),
        GOOGLE_APPLICATION_CREDENTIALS: z.any(),

        // DeGods DeID
        AUTH_DEID_CLIENT_ID: z.string(),
        AUTH_DEID_CLIENT_SECRET: z.string(),

        // STRIPE
        STRIPE_SECRET_KEY: z.string(),
        STRIPE_WEBHOOK_SECRET: z.string(),
        STRIPE_CONNECT_WEBHOOK_SECRET: z.string(),
        STRIPE_WEBHOOK_SECRET_DINNER_APP: z.string().optional(),

        NGROK_URL: z.string().optional(),

        // SENDGRID
        SMTP_HOST: z.string(),
        SMTP_PORT: z.string(),
        SMTP_USER: z.string(),
        SMTP_FROM: z.string(),
        SEND_GRID_API_KEY: z.string(),

        // VONAGE
        VONAGE_API_KEY: z.string(),
        VONAGE_API_SECRET: z.string(),
        VONAGE_PHONE_NUMBER: z.string(),

        // CLOUDFLARE
        CLOUDFLARE_ACCOUNT_ID: z.string(),
        CLOUDFLARE_ACCESS_KEY_ID: z.string(),
        CLOUDFLARE_SECRET_ACCESS_KEY: z.string(),

        // AWS
        AWS_ACCESS_KEY_ID: z.string(),
        AWS_SECRET_ACCESS_KEY: z.string(),
        BUCKET_NAME: z.string(),

        // SENTRY
        SENTRY_AUTH_TOKEN: z.string(),

        // INTERCOM
        INTERCOM_ACCESS_TOKEN: z.string().optional(),

        CF_TURNSTILE_SECRET_KEY: z.string(),

        // AI
        OPENAI_API_KEY: z.string(),
        ANTHROPIC_API_KEY: z.string(),

        // CUSTOMERS - THe FREE PRESS
        THEFP_SUBSTACK_API_KEY: z.string().optional(),
    },

    /**
     * Specify your client-side environment variables schema here. This way you can ensure the app
     * isn't built with invalid env vars. To expose them to the client, prefix them with
     * `NEXT_PUBLIC_`.
     */
    client: {
        // NEXT_PUBLIC_CLIENTVAR: z.string().min(1),
        NEXT_PUBLIC_ASSETS_URL: z.string(),
        // Maps api
        NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string().min(1),
        NEXT_PUBLIC_SOLANA_RPC: z.string().optional(),
        NEXT_PUBLIC_INTERCOM_APP_ID: z.string().optional(),

        NEXT_PUBLIC_CF_TURNSTILE_SITE_KEY: z.string().min(1),
        NEXT_PUBLIC_MIXPANEL_TOKEN: z.string().optional(),

        NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
    },

    /**
     * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
     * middlewares) or client-side so we need to destruct manually.
     */
    runtimeEnv: {
        DATABASE_URL: process.env.DATABASE_URL,
        SLACK_CUSTOMER_NOTIFIER_WEBHOOK_URL:
            process.env.SLACK_CUSTOMER_NOTIFIER_WEBHOOK_URL,
        LOCAL_LONGITUDE: process.env.LOCAL_LONGITUDE,
        LOCAL_LATITUDE: process.env.LOCAL_LATITUDE,
        NODE_ENV: process.env.NODE_ENV,
        VERCEL_ENV: process.env.VERCEL_ENV || "development",
        NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,
        NEXTAUTHJWT_SECRET: process.env.NEXTAUTHJWT_SECRET,
        NEXTAUTH_URL: process.env.NEXTAUTH_URL,
        GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
        GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
        GOOGLE_APPLICATION_CREDENTIALS: process.env
            .GOOGLE_APPLICATION_CREDENTIALS
            ? JSON.parse(
                  Buffer.from(
                      process.env.GOOGLE_APPLICATION_CREDENTIALS,
                      "base64",
                  ).toString(),
              )
            : undefined,
        AUTH_DEID_CLIENT_ID: process.env.AUTH_DEID_CLIENT_ID,
        AUTH_DEID_CLIENT_SECRET: process.env.AUTH_DEID_CLIENT_SECRET,
        NEXT_PUBLIC_GOOGLE_MAPS_API_KEY:
            process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
        STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
        STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
        STRIPE_CONNECT_WEBHOOK_SECRET:
            process.env.STRIPE_CONNECT_WEBHOOK_SECRET,
        NGROK_URL: process.env.NGROK_URL,
        SMTP_HOST: process.env.SMTP_HOST,
        SMTP_PORT: process.env.SMTP_PORT,
        SMTP_USER: process.env.SMTP_USER,
        SMTP_FROM: process.env.SMTP_FROM,
        SEND_GRID_API_KEY: process.env.SEND_GRID_API_KEY,
        VONAGE_API_KEY: process.env.VONAGE_API_KEY,
        VONAGE_API_SECRET: process.env.VONAGE_API_SECRET,
        VONAGE_PHONE_NUMBER: process.env.VONAGE_PHONE_NUMBER,
        CLOUDFLARE_ACCOUNT_ID: process.env.CLOUDFLARE_ACCOUNT_ID,
        CLOUDFLARE_ACCESS_KEY_ID: process.env.CLOUDFLARE_ACCESS_KEY_ID,
        CLOUDFLARE_SECRET_ACCESS_KEY: process.env.CLOUDFLARE_SECRET_ACCESS_KEY,
        AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
        AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
        BUCKET_NAME: process.env.BUCKET_NAME,
        NEXT_PUBLIC_ASSETS_URL: process.env.NEXT_PUBLIC_ASSETS_URL,
        SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
        NEXT_PUBLIC_SOLANA_RPC: process.env.NEXT_PUBLIC_SOLANA_RPC,
        NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
        INTERCOM_ACCESS_TOKEN: process.env.INTERCOM_ACCESS_TOKEN,
        CF_TURNSTILE_SECRET_KEY: process.env.CF_TURNSTILE_SECRET_KEY,
        NEXT_PUBLIC_CF_TURNSTILE_SITE_KEY:
            process.env.NEXT_PUBLIC_CF_TURNSTILE_SITE_KEY,
        NEXT_PUBLIC_MIXPANEL_TOKEN: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN,
        NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
            process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
        OPENAI_API_KEY: process.env.OPENAI_API_KEY,
        ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
        THEFP_SUBSTACK_API_KEY: process.env.THEFP_SUBSTACK_API_KEY,
        STRIPE_WEBHOOK_SECRET_DINNER_APP:
            process.env.STRIPE_WEBHOOK_SECRET_DINNER_APP,
    },
    /**
     * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation.
     * This is especially useful for Docker builds.
     */
    skipValidation: !!process.env.SKIP_ENV_VALIDATION,
});
